// apps/client/src/pages/admin/dashboard/admin/components/AdminRolesDialog.tsx

import { useDialog } from "@/client/stores/dialog";
import { useRoles, useAssignRoleToUser, useRemoveRoleFromUser } from "@/client/services/role/role";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  ScrollArea,
} from "@reactive-resume/ui";
import { useState, useEffect, useMemo } from "react";
import { UserIcon, UsersThreeIcon, XIcon } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { toast } from "sonner";
import { useGetUser } from "@/client/services/user/get-specific-user";

export const AdminRolesDialog = () => {
  const { isOpen, close, payload } = useDialog<{ userId: string; currentRoles: any[] }>(
    "admin-roles",
  );
  const { data: allRoles, isLoading: rolesLoading } = useRoles();
  const { mutate: assignRole, isPending: assigning } = useAssignRoleToUser();
  const { mutate: removeRole, isPending: removing } = useRemoveRoleFromUser();
  const userId = payload?.item?.userId;
  const { user } = useGetUser(userId);
  let currentRoles = user?.roles || [];

  const currentRoleIds = useMemo(
    () =>
      currentRoles
        .map((r: any) => r.id)
        .sort()
        .join(","),
    [currentRoles, userId],
  );

  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelectedRoles(new Set(currentRoles.map((r: any) => r.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoleIds]);

  const handleToggleRole = (roleId: string, checked: boolean) => {
    setSelectedRoles((prev) => {
      const newSet = new Set(prev);
      if (checked) newSet.add(roleId);
      else newSet.delete(roleId);
      return newSet;
    });
  };

  const handleSave = () => {
    console.log(userId);
    if (!userId) return;
    const currentIds = new Set(currentRoles.map((r: any) => r.id));
    const toAdd = [...selectedRoles].filter((id) => !currentIds.has(id));
    const toRemove = [...currentIds].filter((id) => !selectedRoles.has(id));

    const promises: Promise<any>[] = [];
    toAdd.forEach((roleId) => {
      promises.push(
        new Promise((resolve, reject) => {
          assignRole({ userId, roleId }, { onSuccess: resolve, onError: reject });
        }),
      );
    });
    toRemove.forEach((roleId) => {
      promises.push(
        new Promise((resolve, reject) => {
          removeRole({ userId, roleId }, { onSuccess: resolve, onError: reject });
        }),
      );
    });

    Promise.all(promises)
      .then(() => {
        toast.success("Rôles mis à jour avec succès");
        close();
      })
      .catch(() => {
        toast.error("Erreur lors de la mise à jour des rôles");
      });
  };

  const isSaving = assigning || removing;

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent
        className={cn("w-[95vw] max-w-[95vw] overflow-hidden p-0", "sm:w-full sm:max-w-2xl")}
      >
        {/* En-tête */}
        <DialogHeader
          className={cn(
            "flex flex-row items-start gap-3 sm:gap-4",
            "px-4 py-4 sm:px-6 sm:py-5",
            "border-b border-admin-border",
          )}
        >
          <div
            className={cn(
              "flex flex-shrink-0 items-center justify-center rounded-full bg-admin-primary/10 text-admin-primary",
              "h-10 w-10 sm:h-12 sm:w-12",
            )}
          >
            <UsersThreeIcon size={20} weight="duotone" className="sm:hidden" />
            <UsersThreeIcon size={24} weight="duotone" className="hidden sm:block" />
          </div>
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-base font-bold text-admin-text-primary sm:text-xl">
              Gérer les rôles
            </DialogTitle>
            <DialogDescription className="mt-0.5 text-xs text-admin-text-muted sm:mt-1 sm:text-sm">
              Sélectionnez les rôles que vous souhaitez attribuer à:
            </DialogDescription>
          </div>

          {user && (
            <div className="flex flex-col gap-3 rounded-xl bg-admin-surface-elevated p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4">
              <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                <div className="flex-cols flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-admin-surface sm:h-14 sm:w-14">
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <UserIcon size={28} weight="duotone" className="text-admin-text-muted" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-base font-semibold text-admin-text-primary sm:text-lg">
                    {user.name || "—"}
                  </p>
                  <p className="truncate text-xs text-admin-text-muted sm:text-sm">{user.email}</p>
                </div>
              </div>
              {/* <Badge
              variant={user.isActivated ? "success" : "error"}
              className="self-start sm:self-auto text-xs font-medium px-3 py-1 shrink-0"
              outline={false}
            >
              {user.isActivated ? "Actif" : "Banni"}
            </Badge> */}
            </div>
          )}
          {/* <Button
            variant="ghost"
            size="icon"
            onClick={close}
            className="flex-shrink-0 text-admin-text-muted hover:text-admin-text-primary -mt-1 -mr-1 sm:mt-0 sm:mr-0"
          >
            <XIcon size={18} className="sm:hidden" />
            <XIcon size={20} className="hidden sm:block" />
          </Button> */}
        </DialogHeader>

        {/* Liste des rôles */}
        <ScrollArea className="max-h-[60vh] sm:max-h-[65vh]" hideScrollbar={false}>
          <div className="space-y-2 p-4 sm:space-y-3 sm:p-6">
            {rolesLoading ? (
              <div className="py-8 text-center text-admin-text-muted">Chargement des rôles...</div>
            ) : !allRoles || allRoles.length === 0 ? (
              <div className="py-8 text-center text-admin-text-muted">Aucun rôle disponible</div>
            ) : (
              allRoles.map((role) => {
                const isChecked = selectedRoles.has(role.id);
                const isSystemAssigned =
                  role.isSystem && currentRoles.some((r: any) => r.id === role.id);
                //const isDisabled = isSystemAssigned;

                return (
                  <div
                    key={role.id}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isChecked}
                    //aria-disabled={isDisabled}
                    onClick={() => {
                      //if (isDisabled) return;
                      handleToggleRole(role.id, !isChecked);
                    }}
                    onKeyDown={(e) => {
                      //if (isDisabled) return;
                      if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        handleToggleRole(role.id, !isChecked);
                      }
                    }}
                    className={cn(
                      "flex items-start gap-3 rounded-lg border transition-colors sm:gap-4",
                      "p-3 sm:p-4",
                      // Sélectionné → couleur identique au hover
                      isChecked
                        ? "border-admin-border bg-admin-surface-hover"
                        : "border-admin-border bg-admin-surface hover:bg-admin-surface-hover",
                      //isDisabled
                      //  ? "cursor-not-allowed opacity-70"
                      //  : "cursor-pointer"
                    )}
                  >
                    {/* Checkbox : on stoppe la propagation pour éviter le double toggle */}
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="mt-0.5 flex-shrink-0 sm:mt-1"
                    >
                      <Checkbox
                        id={`role-${role.id}`}
                        checked={isChecked}
                        onCheckedChange={(checked) => handleToggleRole(role.id, Boolean(checked))}
                        //disabled={isDisabled}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Label
                          htmlFor={`role-${role.id}`}
                          className={cn(
                            "cursor-pointer break-words text-sm font-semibold text-admin-text-primary sm:text-base",
                            //isDisabled && "cursor-not-allowed opacity-70"
                          )}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {role.name}
                        </Label>
                        {role.isSystem && (
                          <Badge className="flex-shrink-0 bg-admin-surface-elevated text-[10px] text-admin-text-muted sm:text-xs">
                            (système)
                          </Badge>
                        )}
                      </div>
                      {role.description && (
                        <p className="mt-1 break-words text-xs text-admin-text-muted sm:text-sm">
                          {role.description}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>

        {/* Pied de page */}
        <DialogFooter
          className={cn(
            "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3",
            "px-4 py-3 sm:px-6 sm:py-4",
            "border-t border-admin-border",
          )}
        >
          <Button variant="ghost" onClick={close} disabled={isSaving} className="w-full sm:w-auto">
            Annuler
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full bg-admin-primary text-admin-primary-foreground hover:bg-admin-primary/90 sm:w-auto sm:min-w-[120px]"
          >
            {isSaving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
